import React, { useState, useRef, useEffect } from 'react';
import { t } from 'i18next';

import searchIcon from '../../../../assets/icons/search.svg';
import closeIcon  from '../../../../assets/icons/close.svg';

import monitoringService from '../../../../services/monitoringService';
import { getSuggestions } from '../../../../api/searchSuggations';
import SearchResponse from './SearchResponse';
import SearchStart    from './SearchStart';
import SearchError    from './SearchError';

const GREEN_FILTER = '[filter:brightness(0)_saturate(100%)_invert(18%)_sepia(70%)_saturate(6415%)_hue-rotate(142deg)_brightness(93%)_contrast(84%)]';
const GREY_FILTER  = '[filter:brightness(0)_saturate(100%)_invert(55%)_sepia(0%)_saturate(0%)_brightness(90%)]';
const WHITE_FILTER = '[filter:brightness(0)_invert(1)]';

function SearchPanel({ onClose }) {
    const [query,   setQuery]   = useState('');
    const [results, setResults] = useState(null);   // null=idle, []=no results, [...]=results
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.focus(); }, []);

    const doSearch = async (q) => {
        const trimmed = q.trim();
        if (!trimmed) { setResults(null); return; }

        setLoading(true);
        setError('');
        try {
            monitoringService.trackSearch(trimmed).catch(() => {});
            const res = await getSuggestions(trimmed);
            const list = Array.isArray(res) ? res : (res.announcements || []);
            setResults(list);
        } catch {
            setError(t('error') || 'Қате орын алды');
            setResults(null);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        if (!val.trim()) { setResults(null); setError(''); }
    };

    const handleKey = (e) => {
        if (e.key === 'Enter') doSearch(query);
    };

    const handleClear = () => {
        setQuery('');
        setResults(null);
        setError('');
        inputRef.current?.focus();
    };

    return (
        <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.2)] overflow-hidden">

            {/* ── Search bar ──────────────────────────────────── */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EDEDED]">

                {/* Search icon */}
                <img
                    src={searchIcon}
                    alt=""
                    width={20}
                    height={20}
                    className={`shrink-0 transition-colors duration-150 ${
                        query ? GREEN_FILTER : GREY_FILTER
                    }`}
                />

                {/* Input */}
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={handleChange}
                    onKeyDown={handleKey}
                    placeholder={t('searchPlaceholder')}
                    className="
                        flex-1 bg-transparent outline-none border-none
                        text-[15px] text-[#222222] placeholder-[#939393]
                        py-2
                    "
                />

                {/* Clear button */}
                {query && (
                    <button
                        onClick={handleClear}
                        className="
                            w-7 h-7 rounded-full bg-[#F9F9F9] hover:bg-[#EDEDED]
                            flex items-center justify-center shrink-0
                            border-none cursor-pointer transition-colors duration-150
                        "
                    >
                        <img src={closeIcon} alt="clear" width={12} height={12} className={GREY_FILTER} />
                    </button>
                )}

                {/* Search button */}
                <button
                    onClick={() => doSearch(query)}
                    disabled={loading}
                    className="
                        shrink-0 h-9 px-4 rounded-xl
                        bg-[#147F26] hover:bg-[#7AB30E] disabled:bg-[#EDEDED]
                        border-none cursor-pointer transition-colors duration-150
                        flex items-center gap-2
                    "
                >
                    {loading ? (
                        <svg className="w-4 h-4 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"/>
                            <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                    ) : (
                        <img src={searchIcon} alt="search" width={16} height={16} className={WHITE_FILTER} />
                    )}
                    <span className="text-white text-[13px] font-semibold hidden sm:block">
                        {t('search') || 'Іздеу'}
                    </span>
                </button>

                {/* Close modal */}
                <button
                    onClick={onClose}
                    className="
                        shrink-0 w-9 h-9 rounded-xl
                        bg-[#F9F9F9] hover:bg-[#EDEDED]
                        border-none cursor-pointer transition-colors duration-150
                        flex items-center justify-center
                    "
                >
                    <img src={closeIcon} alt="close" width={16} height={16} className={GREY_FILTER} />
                </button>
            </div>

            {/* ── Body ────────────────────────────────────────── */}
            <div className="max-h-[60vh] overflow-y-auto [scrollbar-width:thin]">
                {error ? (
                    <SearchError message={error} />
                ) : results === null ? (
                    <SearchStart />
                ) : results.length === 0 ? (
                    <SearchError message={t('noResults') || 'Нәтиже табылмады'} />
                ) : (
                    <SearchResponse announcements={results} onClose={onClose} />
                )}
            </div>

            {/* ── Footer hint ─────────────────────────────────── */}
            <div className="px-4 py-2 border-t border-[#EDEDED] flex items-center gap-3">
                <span className="text-[11px] text-[#939393]">
                    <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F9F9F9] border border-[#EDEDED] rounded font-mono">Enter</kbd>
                    {' '}{t('search') || 'іздеу'}
                </span>
                <span className="text-[11px] text-[#939393]">
                    <kbd className="px-1.5 py-0.5 text-[10px] bg-[#F9F9F9] border border-[#EDEDED] rounded font-mono">Esc</kbd>
                    {' '}{t('close') || 'жабу'}
                </span>
            </div>
        </div>
    );
}

export default SearchPanel;
